import React from 'react';
import Script from 'next/script';
import { useRouter } from 'next/router';
import { gql } from '@apollo/client';

import Loading from '../../components/Loading';
import Seo from '../../components/Seo';
import SeoFooter from '../../components/SeoFooter';
import DataLoadingAndErrorUI from '../../components/DataLoadingAndErrorUI';
import TitlePromoBanner from '../../components/UplpPromoBanner/TitlePromoBanner';
import CardsGrid, { CardsGridFragments } from '../../components/CardsGrid';
import GenericNoResults from '../../components/GenericNoResults';

import useAllCardsQuery from '../../hooks/useAllCardsQuery';
import { useFavoritePackageIdsQuery } from '../../hooks/useFavoritePackageIdsQuery';
import getAppUrl from '../../utils/getAppUrl';

import { getPLPCanonicalQuery, getPlpSeoTitleSuffix } from '../../utils/getSeoProps';
import PageData from '../../assets/LandingPages/Cards';

const ALL_CARDS_QUERY = gql`
  query allCards(
    $version: String!
    $page: Int!
    $pageSize: Int!
    $sortBy: String
    $sortDirection: SortDirection
    $ages: [String]
    $colors: [String]
    $coins: [String]
    $designers: [String]
    $greetings: [String]
    $photoCounts: [String]
    $shapes: [String]
    $themes: [String]
  ) {
    cards: search(
      productType: card
      version: $version
      page: $page
      pageSize: $pageSize
      sortBy: $sortBy
      sortDirection: $sortDirection
      ages: $ages
      colors: $colors
      coins: $coins
      designers: $designers
      greetings: $greetings
      photoCounts: $photoCounts
      shapes: $shapes
      themes: $themes
    ) {
      ...CardsGridCardsFragment
    }
  }
  ${CardsGridFragments.cards}
`;

function Cards() {
  const { query } = useRouter();
  const { favoritePackageIds } = useFavoritePackageIdsQuery();
  const { cards, error, loading } = useAllCardsQuery(ALL_CARDS_QUERY);
  const { FAQ_SCHEMA, seo } = PageData;
  const seoProps = {
    title: `All Cards | ${getPlpSeoTitleSuffix(true)}`,
    description: '',
    canonicalUrl: `${getAppUrl()}/cards${getPLPCanonicalQuery(query)}`,
    url: `${getAppUrl()}/cards${getPLPCanonicalQuery(query)}`,
  };

  if ((loading && !cards) || error) {
    return <DataLoadingAndErrorUI error={error} loading={loading && !cards} seoProps={seoProps} />;
  }

  return (
    <>
      {loading && <Loading overlay />}
      <Seo {...seoProps} />
      <TitlePromoBanner title="All Cards" />
      <div id="content-grid">
        <CardsGrid
          cards={cards}
          favoritePackageIds={favoritePackageIds}
          hasCrossSelling={false}
          hideAColumnNavs={false}
          hideCardsFilterNav={false}
          isActive
          isDefault
          noResults={cards?.edges?.length <= 0 ? GenericNoResults : undefined}
        />
      </div>
      <SeoFooter markdown={seo.footer} />
      <Script id="faq-schema" type="application/ld+json">
        {JSON.stringify(FAQ_SCHEMA)}
      </Script>
    </>
  );
}

export default Cards;
